import * as React from "react";
import { CircleAlert } from "./icons";

import { cn } from "utils/cn";
import { useStableId } from "./useStableId";

interface FormFieldContextValue {
  id: string;
  describedBy?: string;
  invalid: boolean;
  required: boolean;
}

const FormFieldContext = React.createContext<FormFieldContextValue | null>(null);

interface ControlAriaProps {
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false" | "grammar" | "spelling";
  required?: boolean;
}

/**
 * Wires a control to its surrounding FormField: id for the label, aria-describedby for the hint
 * or error, aria-invalid when there is an error. Props passed explicitly win.
 */
export const useFormFieldControl = <T extends ControlAriaProps>(props: T): T => {
  const field = React.useContext(FormFieldContext);
  if (!field) {
    return props;
  }
  return {
    ...props,
    id: props.id || field.id,
    "aria-describedby":
      [field.describedBy, props["aria-describedby"]].filter(Boolean).join(" ") || undefined,
    "aria-invalid": props["aria-invalid"] ?? (field.invalid || undefined),
    required: props.required ?? (field.required || undefined),
  };
};

export interface LabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  required?: boolean;
}

export const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  ({ className, required, children, ...props }, ref) => (
    <label
      ref={ref}
      data-ui="label"
      className={cn("text-sm font-medium leading-none text-foreground", className)}
      {...props}
    >
      {children}
      {required && (
        <span aria-hidden="true" className="ml-0.5 text-danger">
          *
        </span>
      )}
    </label>
  )
);
Label.displayName = "Label";

export interface FormFieldProps {
  label: React.ReactNode;
  /** Shown under the control when there is no error. */
  hint?: React.ReactNode;
  /** Error text; also marks the control invalid. */
  error?: React.ReactNode;
  required?: boolean;
  /** Small element on the label row, e.g. a "Forgot password?" link. */
  labelAction?: React.ReactNode;
  /** Explicit control id; generated when omitted. */
  id?: string;
  className?: string;
  children: React.ReactNode;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  hint,
  error,
  required = false,
  labelAction,
  id: explicitId,
  className,
  children,
}) => {
  const id = useStableId("field", explicitId);
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const invalid = Boolean(error);
  const describedBy = invalid ? errorId : hint ? hintId : undefined;

  const value = React.useMemo(
    () => ({ id, describedBy, invalid, required }),
    [id, describedBy, invalid, required]
  );

  return (
    <FormFieldContext.Provider value={value}>
      <div data-ui="form-field" className={cn("flex flex-col gap-2", className)}>
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor={id} required={required}>
            {label}
          </Label>
          {labelAction}
        </div>
        {children}
        {invalid ? (
          <p id={errorId} className="flex items-start gap-1.5 text-xs font-medium text-danger-text">
            <CircleAlert aria-hidden="true" className="mt-px h-3.5 w-3.5 shrink-0" />
            <span>{error}</span>
          </p>
        ) : (
          hint && (
            <p id={hintId} className="text-xs text-muted-foreground">
              {hint}
            </p>
          )
        )}
      </div>
    </FormFieldContext.Provider>
  );
};
